import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { ButtonBase, Checkbox, Text } from '@belong/ui';
import { IconTrash, IconReceiptWarning } from '@belong/icons';
import { PaymentPreference } from 'models/enums';
import { CopyToClipboardButton } from 'pages/Accounts/Components/copy-to-clipboard-button';
import type { PaymentAccountResourceModel } from 'api/models';
import type {
  ConsumerFailedPaymentModel,
  ExternalPaymentMethod,
  ConsumerWireInstructionsModel,
} from '../failed-payments.types';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import {
  formatNegativeFailedPaymentsAmount,
  formatAddressWithoutStateAndZip,
  getExternalReceiptFileError,
  getExternalPaymentHomeKey,
  getBalanceUniqueIds,
  getFailedPaymentsTotalForBalanceIds,
  mapPaymentAccountToPaymentMethodSummary,
} from '../failed-payments-utils';
import type { ExternalReceiptFileError as WireFileError } from '../failed-payments-utils';
import { FailedPaymentMethodSelector } from '../failed-payment-method-selector';

const WIRE_RECEIPT_ACCEPT = '.png,.jpg,.jpeg,.pdf';

function getWireErrorTitle(error: WireFileError): string {
  const template = error.empty
    ? FAILED_PAYMENTS_STRINGS['external.upload_error_empty']
    : error.tooBig && error.unreadable
      ? FAILED_PAYMENTS_STRINGS['wire.upload_error_too_big_unreadable']
      : error.tooBig
        ? FAILED_PAYMENTS_STRINGS['wire.upload_error_too_big']
        : FAILED_PAYMENTS_STRINGS['wire.upload_error_unreadable'];

  return template
    .replace('{filename}', error.filename)
    .replace('{size}', error.size)
    .replace('{extension}', error.extension);
}

export type HomeownerChangePaymentMethodSubsectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  paymentMethods: PaymentAccountResourceModel[];
  selectedPaymentAccountUniqueId: string | null;
  externalPaymentMethod: ExternalPaymentMethod | null;
  canPayByWire: boolean;
  canPayByZelle: boolean;
  onSelectPaymentMethod: (id: string) => void;
  onSelectExternalPaymentMethod: (method: ExternalPaymentMethod) => void;
  wireInstructions: ConsumerWireInstructionsModel | null;
  wireInstructionsLoading: boolean;
  wireInstructionsError?: boolean;
  onRetryWireInstructions?: () => void;
  wireCoveringBalanceUniqueIds: string[];
  onToggleWireCovering: (balanceUniqueIds: string[]) => void;
  wireReceiptFile: File | null;
  onWireReceiptChange: (file: File | null) => void;
};

export function HomeownerChangePaymentMethodSubsection({
  failedPayments,
  paymentMethods,
  selectedPaymentAccountUniqueId,
  externalPaymentMethod,
  canPayByWire,
  canPayByZelle,
  onSelectPaymentMethod,
  onSelectExternalPaymentMethod,
  wireInstructions,
  wireInstructionsLoading,
  wireInstructionsError,
  onRetryWireInstructions,
  wireCoveringBalanceUniqueIds,
  onToggleWireCovering,
  wireReceiptFile,
  onWireReceiptChange,
}: HomeownerChangePaymentMethodSubsectionProps) {
  const selectedPaymentMethod = paymentMethods.find(
    (paymentMethod) =>
      paymentMethod.isVerified &&
      paymentMethod.accountId === selectedPaymentAccountUniqueId,
  );
  const selectedSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : null;

  return (
    <div className="flex w-full flex-col gap-2xl">
      <div className="flex w-full flex-col gap-sm">
        <Text
          variant="h3"
          fontWeight="semibold"
          className="w-full text-h3-xl leading-[normal]"
        >
          {FAILED_PAYMENTS_STRINGS['change_payment_method.homeowner.prompt']}
        </Text>

        <FailedPaymentMethodSelector
          paymentMethod={selectedSummary}
          selectedPaymentAccountUniqueId={selectedPaymentAccountUniqueId}
          paymentType={PaymentPreference.Maintenance}
          onSelectPaymentMethod={onSelectPaymentMethod}
          externalPaymentMethod={externalPaymentMethod}
          onSelectWireTransfer={
            canPayByWire
              ? () => onSelectExternalPaymentMethod('WireTransfer')
              : undefined
          }
          onSelectZelleTransfer={
            canPayByZelle
              ? () => onSelectExternalPaymentMethod('ZelleTransfer')
              : undefined
          }
        />
      </div>

      {externalPaymentMethod && (
        <WireDetails
          key={externalPaymentMethod}
          externalPaymentMethod={externalPaymentMethod}
          failedPayments={failedPayments}
          wireInstructions={wireInstructions}
          wireInstructionsLoading={wireInstructionsLoading}
          wireInstructionsError={wireInstructionsError}
          onRetryWireInstructions={onRetryWireInstructions}
          wireCoveringBalanceUniqueIds={wireCoveringBalanceUniqueIds}
          onToggleWireCovering={onToggleWireCovering}
          wireReceiptFile={wireReceiptFile}
          onWireReceiptChange={onWireReceiptChange}
        />
      )}
    </div>
  );
}

export function WireDetails({
  externalPaymentMethod,
  failedPayments,
  wireInstructions,
  wireInstructionsLoading,
  wireInstructionsError,
  onRetryWireInstructions,
  wireCoveringBalanceUniqueIds,
  onToggleWireCovering,
  wireReceiptFile,
  onWireReceiptChange,
}: Pick<
  HomeownerChangePaymentMethodSubsectionProps,
  | 'externalPaymentMethod'
  | 'failedPayments'
  | 'wireInstructions'
  | 'wireInstructionsLoading'
  | 'wireInstructionsError'
  | 'onRetryWireInstructions'
  | 'wireCoveringBalanceUniqueIds'
  | 'onToggleWireCovering'
  | 'wireReceiptFile'
  | 'onWireReceiptChange'
>) {
  const isZelle = externalPaymentMethod === 'ZelleTransfer';
  const [fileError, setFileError] = useState<WireFileError | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function handleFile(file: File | undefined) {
    if (!file) return;
    const error = getExternalReceiptFileError(file);
    setFileError(error);
    onWireReceiptChange(error ? null : file);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    handleFile(event.target.files?.[0]);
    event.target.value = '';
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    handleFile(event.dataTransfer.files?.[0]);
  }

  const bankRows = wireInstructions
    ? ([
        [
          FAILED_PAYMENTS_STRINGS['wire.account_holder'],
          wireInstructions.accountHolder,
        ],
        [FAILED_PAYMENTS_STRINGS['wire.bank'], wireInstructions.bankName],
        [
          FAILED_PAYMENTS_STRINGS['wire.bank_address'],
          wireInstructions.bankAddress,
        ],
        [
          FAILED_PAYMENTS_STRINGS['wire.routing_number'],
          wireInstructions.routingNumber,
        ],
        [
          FAILED_PAYMENTS_STRINGS['wire.account_number'],
          wireInstructions.accountNumber,
        ],
      ] as const)
    : [];

  const showHomeAddress =
    new Set(failedPayments.map(getExternalPaymentHomeKey)).size > 1;

  const coveredTotal = getFailedPaymentsTotalForBalanceIds(
    failedPayments,
    wireCoveringBalanceUniqueIds,
  );

  return (
    <div className="flex w-full flex-col gap-2xl">
      <div className="flex w-full flex-col gap-sm">
        <Text
          variant="h3"
          fontWeight="semibold"
          className="w-full text-h3-xl leading-[normal]"
        >
          {FAILED_PAYMENTS_STRINGS['wire.where_title']}
        </Text>

        {wireInstructionsLoading ? (
          <Text variant="body" className="text-navy">
            {FAILED_PAYMENTS_STRINGS['unable_to_pay.partial.preview_loading']}
          </Text>
        ) : wireInstructions && isZelle ? (
          <div className="flex w-full flex-col gap-sm rounded border border-solid border-gray bg-light-gray p-sm">
            {[
              [
                FAILED_PAYMENTS_STRINGS['zelle.recipient'],
                wireInstructions.recipient,
              ],
              [FAILED_PAYMENTS_STRINGS['zelle.note'], wireInstructions.note],
            ].map(([label, value]) =>
              value ? (
                <div
                  key={label}
                  className="flex w-full items-center justify-between gap-md"
                >
                  <Text className="min-w-0 break-words leading-[20px] text-navy">
                    <span className="font-semibold">{label}</span> {value}
                  </Text>
                  <CopyToClipboardButton value={value} />
                </div>
              ) : null,
            )}
          </div>
        ) : wireInstructions ? (
          <div className="flex flex-col">
            <div className="-mb-px flex items-center justify-between rounded-t border border-solid border-gray bg-light-gray p-sm">
              <div className="flex min-w-0 flex-1 flex-col gap-xs">
                <Text
                  fontWeight="semibold"
                  className="leading-[1.541] text-navy"
                >
                  {FAILED_PAYMENTS_STRINGS['wire.reference_code']}
                </Text>
                <Text
                  variant="h2"
                  fontWeight="semibold"
                  className="break-all text-green sm:text-h2-xl sm:leading-h2-xl"
                >
                  {wireInstructions.referenceCode}
                </Text>
                <Text variant="p1" className="leading-[1.541] text-navy">
                  {FAILED_PAYMENTS_STRINGS['wire.reference_hint']}
                </Text>
              </div>
              <CopyToClipboardButton value={wireInstructions.referenceCode} />
            </div>
            <div className="flex flex-col gap-sm rounded-b border border-solid border-gray bg-white p-sm">
              {bankRows.map(([label, value]) => (
                <div
                  key={label}
                  className="flex w-full items-center justify-between gap-md"
                >
                  <Text className="min-w-0 break-words leading-[20px] text-navy">
                    <span className="font-semibold">{label}</span> {value}
                  </Text>
                  <CopyToClipboardButton value={value} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-xs">
            <Text variant="p1" className="text-red">
              {
                FAILED_PAYMENTS_STRINGS[
                  wireCoveringBalanceUniqueIds.length
                    ? 'external.instructions_error'
                    : 'external.select_covering'
                ]
              }
            </Text>
            {wireInstructionsError && onRetryWireInstructions && (
              <ButtonBase
                onClick={onRetryWireInstructions}
                className="text-green"
              >
                {FAILED_PAYMENTS_STRINGS['external.retry']}
              </ButtonBase>
            )}
          </div>
        )}
      </div>

      <div className="flex w-full flex-col gap-sm">
        <Text
          variant="h3"
          fontWeight="semibold"
          className="w-full text-h3-xl leading-[normal]"
        >
          {
            FAILED_PAYMENTS_STRINGS[
              isZelle ? 'zelle.covering_title' : 'wire.covering_title'
            ]
          }
        </Text>
        <div className="flex w-full flex-col gap-sm">
          {failedPayments.map((failedPayment) => {
            const balanceIds = getBalanceUniqueIds(failedPayment);
            const balanceUniqueId = balanceIds[0];
            if (!balanceUniqueId) return null;
            const isSelected = balanceIds.some((id) =>
              wireCoveringBalanceUniqueIds.includes(id),
            );

            return (
              <div
                key={balanceUniqueId}
                className="flex w-full items-center justify-between gap-md"
              >
                <Checkbox
                  id={`wire-covering-${balanceUniqueId}`}
                  checked={isSelected}
                  label={
                    showHomeAddress && failedPayment.homeAddress
                      ? `${failedPayment.label ?? ''} · ${formatAddressWithoutStateAndZip(failedPayment.homeAddress)}`
                      : (failedPayment.label ?? '')
                  }
                  onChange={() => onToggleWireCovering(balanceIds)}
                  fontWeight="regular"
                  wrapperClassName="min-w-0"
                  inputContainerClass="shrink-0"
                  labelClassName="block min-w-0 overflow-hidden"
                  labelTextClassName="truncate leading-[20px]"
                />
                <Text
                  fontWeight="semibold"
                  className="shrink-0 text-right leading-[20px] text-navy"
                >
                  {formatNegativeFailedPaymentsAmount(failedPayment.amount)}
                </Text>
              </div>
            );
          })}
        </div>

        <div className="flex w-full items-center justify-between border-0 border-t border-dashed border-gray pt-sm">
          <Text fontWeight="semibold" className="leading-[20px]">
            {FAILED_PAYMENTS_STRINGS['section.total']}
          </Text>
          <Text fontWeight="semibold" className="text-right leading-[20px]">
            {formatNegativeFailedPaymentsAmount(coveredTotal)}
          </Text>
        </div>
      </div>

      <div className="flex w-full flex-col gap-sm">
        <Text
          variant="h3"
          fontWeight="semibold"
          className="w-full text-h3-xl leading-[normal]"
        >
          {
            FAILED_PAYMENTS_STRINGS[
              isZelle ? 'zelle.upload_title' : 'wire.upload_title'
            ]
          }
        </Text>

        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className="flex h-[200px] w-full cursor-pointer flex-col items-center justify-center gap-[2px] rounded border-2 border-dashed border-gray bg-light-gray"
        >
          <span className="inline-flex h-[96px] w-[96px] shrink-0">
            <img
              src="/icons/failed-payments/upload.svg"
              alt=""
              width={96}
              height={96}
              className="block h-full w-full"
            />
          </span>
          <Text
            fontWeight="semibold"
            className="text-center leading-[20px] text-navy"
          >
            {FAILED_PAYMENTS_STRINGS['wire.upload_cta']}
          </Text>
          <Text
            variant="p1"
            className="text-center leading-[15px] text-dark-gray"
          >
            {FAILED_PAYMENTS_STRINGS['wire.upload_hint']}
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept={WIRE_RECEIPT_ACCEPT}
            className="hidden"
            onChange={handleInputChange}
          />
        </div>

        {wireReceiptFile && (
          <div className="flex w-full items-center justify-between gap-md">
            <Text
              fontWeight="semibold"
              className="min-w-0 truncate leading-[20px] text-navy"
            >
              {wireReceiptFile.name}
            </Text>
            <button
              type="button"
              aria-label={
                FAILED_PAYMENTS_STRINGS['external.remove_receipt_label']
              }
              onClick={() => onWireReceiptChange(null)}
              className="cursor-pointer border-0 bg-transparent p-0 text-red"
            >
              <IconTrash width={14} height={18} />
            </button>
          </div>
        )}
      </div>

      {fileError && (
        <div className="flex w-full flex-col gap-xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-sm">
          <span className="flex items-start gap-xs">
            <span className="inline-flex h-[20px] w-[22px] shrink-0">
              <IconReceiptWarning
                aria-hidden="true"
                width={22}
                height={20}
                className="block h-full w-full"
              />
            </span>
            <Text
              fontWeight="semibold"
              className="min-w-0 break-words leading-[20px] text-navy"
            >
              {getWireErrorTitle(fileError)}
            </Text>
          </span>
          <Text variant="p1" className="leading-[15px] text-navy">
            {FAILED_PAYMENTS_STRINGS['wire.upload_error_hint']}
          </Text>
        </div>
      )}
    </div>
  );
}
